import { Action, LoopOnItemsAction, RouterAction } from '../actions/action'
import { Connection } from '../connections/connection'
import { FlowVersion, FlowVersionState } from '../flow-version'
import { Trigger } from '../triggers/trigger'
import { flowStructureUtil } from '../util/flow-structure-util'
import { FlowOperationRequest, FlowOperationType, ImportFlowRequest, StepLocationRelativeToParent } from './index'

function createDeleteActionOperation(actionName: string): FlowOperationRequest {
    return {
        type: FlowOperationType.DELETE_ACTION,
        request: { names: [actionName] },
    }
}

function createUpdateTriggerOperation(trigger: Trigger): FlowOperationRequest {
    return {
        type: FlowOperationType.UPDATE_TRIGGER,
        request: trigger,
    }
}

function createChangeNameOperation(displayName: string): FlowOperationRequest {
    return {
        type: FlowOperationType.CHANGE_NAME,
        request: { displayName },
    }
}

type FlowContext = {
    type: 'loop' | 'branch' | 'main'
    connections: Connection[]
    locationType: StepLocationRelativeToParent
    parentId?: string
    branchIndex?: number
    isBranchOptions?: boolean
}

type PlacementResult = {
    parentStep: string[]
    location: StepLocationRelativeToParent
}

function _getImportOperations(
    flowVersion: FlowVersion,
    branchOptions?: { routerStep: RouterAction, branchIndex: number },
): FlowOperationRequest[] {
    const operations: FlowOperationRequest[] = []
    const processedActions = new Set<string>()

    const allActions = flowStructureUtil.getAllStepsInExecutionOrder(flowVersion)
        .filter(step => flowStructureUtil.isAction(step.type)) as Action[]

    allActions.forEach((action, index) => {
        if (processedActions.has(action.name)) return
        processedActions.add(action.name)

        const context = determineContext(action)
        const actionToInsert = removeAnySubsequentAction(action)
        const placement = getPlacement(action, context, index, operations)

        operations.push({
            type: FlowOperationType.ADD_ACTION,
            request: {
                parentStep: placement.parentStep,
                stepLocationRelativeToParent: placement.location,
                branchIndex: context.type === 'branch' ? context.branchIndex : undefined,
                action: actionToInsert,
            },
        })
    })

    return operations

    function determineContext(action: Action): FlowContext {
        const loopParent = flowStructureUtil.getParentLoopStepName(flowVersion, action.name)
        if (loopParent) {
            const loopStep = flowStructureUtil.getStepOrThrow(loopParent, flowVersion) as LoopOnItemsAction
            return { type: 'loop', parentId: loopParent, connections: loopStep.subflow?.connections ?? [], locationType: StepLocationRelativeToParent.INSIDE_LOOP }
        }

        const routerParent = flowStructureUtil.getParentRouterStepNameAndBranchIndex(flowVersion, action.name)
        if (routerParent || branchOptions) {
            const routerStep = routerParent ? flowStructureUtil.getStepOrThrow(routerParent.routerParentName, flowVersion) as RouterAction : branchOptions?.routerStep
            const branchIndex = routerParent?.branchIndex ?? branchOptions?.branchIndex
            const connections = branchOptions ? flowVersion.connections : routerStep?.children?.[branchIndex ?? 0]?.connections

            return { 
                type: 'branch', 
                parentId: routerStep?.name, 
                connections: connections ?? [], 
                locationType: StepLocationRelativeToParent.INSIDE_BRANCH,
                branchIndex,
                isBranchOptions: !!branchOptions, 
            }
        }

        return { type: 'main', connections: [], locationType: StepLocationRelativeToParent.AFTER }
    }

    function getPlacement(action: Action, ctx: FlowContext, index: number, currentOps: FlowOperationRequest[]): PlacementResult {
        const isFirst = ctx.type === 'branch' && ctx.isBranchOptions 
            ? index === 0 
            : checkIsFirstInContainer(action.name, ctx)

        if (isFirst && ctx.parentId) {
            return { parentStep: [ctx.parentId], location: ctx.locationType }
        }

        // Try to find the parent via existing connections (Middle/Tail steps)
        const sourceId = ctx.connections.find(c => c.targetId === action.name)?.sourceId
        if (sourceId) {
            return { parentStep: [sourceId], location: StepLocationRelativeToParent.AFTER }
        }

        // Fallback for Branch Duplication: link to the last added action in the list
        if (ctx.isBranchOptions) {
            const lastActionName = (currentOps[currentOps.length - 1]?.request as any)?.action?.name
            if (lastActionName) return { parentStep: [lastActionName], location: StepLocationRelativeToParent.AFTER }
        }

        
        return { 
            parentStep: flowStructureUtil.getStepParents(flowVersion, action.name), 
            location: StepLocationRelativeToParent.AFTER, 
        }
    }

    function checkIsFirstInContainer(name: string, ctx: FlowContext): boolean {
        if (ctx.type === 'loop' && ctx.parentId) return flowStructureUtil.getLoopFirstStepName(flowVersion, ctx.parentId) === name
        if (ctx.type === 'branch' && ctx.parentId && ctx.branchIndex !== undefined) return flowStructureUtil.getBranchFirstStepName(flowVersion, ctx.parentId, ctx.branchIndex) === name
        return false
    }
}

export function removeAnySubsequentAction(action: Action): Action {
    return {
        ...action,
        nextAction: undefined,
    }
}

function _importFlow(flowVersion: FlowVersion, request: ImportFlowRequest): FlowOperationRequest[] {
    const existingActions = flowStructureUtil.getAllNextActionsWithoutChildren(flowVersion.triggers[0], flowVersion)

    const deleteOperations = existingActions.map(action =>
        createDeleteActionOperation(action.name),
    )

    const importOperations = _getImportOperations({
        ...request,
        id: flowVersion.id,
        flowId: flowVersion.flowId,
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
        state: FlowVersionState.DRAFT,
        valid: true
    })

    return [
        createChangeNameOperation(request.displayName),
        ...deleteOperations,
        createUpdateTriggerOperation(request.triggers[0]),
        ...importOperations,
    ]
}

export { _importFlow, _getImportOperations }
